import { useState, type FormEvent } from "react";
import type { ParticipationPromptPublic } from "@tih/shared";
import { ApiError, postParticipationPastoral } from "../../api/client";
import { useBotProtection } from "../../hooks/useBotProtection";
import { useSiteCopy } from "../../context/SiteCopyContext";
import { getAtPath } from "../../lib/siteCopyPaths";
import { HoneypotField } from "../forms/HoneypotField";
import { TurnstileWidget } from "../forms/TurnstileWidget";

type Props = {
  prompt: ParticipationPromptPublic;
  onSuccess: () => void;
};

export function ParticipationPastoralForm({ prompt, onSuccess }: Props) {
  const { copy } = useSiteCopy();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [body, setBody] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const {
    config,
    needsTurnstile,
    ready,
    website,
    setWebsite,
    botFields,
    onTurnstileToken,
    onTurnstileExpire,
    registerTurnstileReset,
    resetBotProtection,
  } = useBotProtection();

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!ready || busy) return;
    setError(null);
    setBusy(true);
    try {
      await postParticipationPastoral({
        promptSlug: prompt.slug,
        name: name.trim(),
        phone: phone.trim(),
        body: body.trim() || undefined,
        ...botFields,
      });
      setDone(true);
      onSuccess();
    } catch (err) {
      if (err instanceof ApiError) setError(err.message);
      else setError("Could not send your request");
      resetBotProtection();
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <div className="form-success">
        <strong>{String(getAtPath(copy, "home.participation.pastoralThanks") ?? "")}</strong>
      </div>
    );
  }

  return (
    <form onSubmit={(e) => void onSubmit(e)} className="form-stack">
      <HoneypotField value={website} onChange={setWebsite} />
      <label>
        <span>Your name</span>
        <input
          type="text"
          required
          maxLength={120}
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={busy}
        />
      </label>
      <label>
        <span>Phone / WhatsApp</span>
        <input
          type="tel"
          required
          maxLength={40}
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          disabled={busy}
        />
      </label>
      <label>
        <span>Message (optional)</span>
        <textarea
          maxLength={500}
          rows={4}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          disabled={busy}
          placeholder={prompt.placeholder ?? ""}
        />
      </label>
      {needsTurnstile && config?.turnstileSiteKey && (
        <TurnstileWidget
          siteKey={config.turnstileSiteKey}
          onToken={onTurnstileToken}
          onExpire={onTurnstileExpire}
          onRegisterReset={registerTurnstileReset}
        />
      )}
      {error && <div className="form-error">{error}</div>}
      <button type="submit" className="btn btn-primary" disabled={busy || !ready}>
        {busy ? "Sending…" : "Send"}
      </button>
    </form>
  );
}
